<template>
    <div>
        <b-card no-body>
            <template #header>
                <div class="row mt-1">
                    <div class="col-3">
                        <CardID :id="identifier.id" :type="identifier.type"/>
                        <div class="d-flex">
                            <div><b-badge class="mr-2" v-if="slideNumber">Slide {{ slideNumber }}</b-badge></div>
                            <div class="text-capitalize">{{ formattedType }}</div>
                            <div class="ml-2">
                                <b-btn
                                    v-if="hasRasterProblems"
                                    @click="showRasterInfoPopup"
                                    size="sm"
                                    variant="outline-warning"
                                >
                                    <font-awesome-icon :icon="['fad', 'info-circle']" />
                                </b-btn>
                            </div>
                        </div>
                    </div>
                    <div class="col-6">
                        <b-input-group>
                            <b-input-group-prepend is-text>
                                <b-form-checkbox
                                    :checked="item.slide_title_enabled"
                                    switch
                                    class="mr-n2"
                                    :disabled="disabled"
                                    @change="(checked) => updateSlide({...item, slide_title_enabled: checked})"
                                >
                                    Title
                                </b-form-checkbox>
                            </b-input-group-prepend>
                            <b-form-input
                                :value="item.slide_title"
                                :placeholder="(disabled || !item.slide_title_enabled) ? '' : 'no slide title'"
                                :disabled="disabled || !item.slide_title_enabled"
                                debounce="500"
                                @update="(value) => updateSlide({...item, slide_title: value})"
                            />
                        </b-input-group>
                    </div>
                    <div class="col-3 d-flex justify-content-end">
                        <b-form-checkbox
                            class="mr-3 mt-1"
                            size="sm"
                            :checked="item.teacher_only"
                            :disabled="disabled"
                            switch
                            @change="(checked) => updateSlide({...item, teacher_only: checked})"
                        >
                            Teacher Only
                        </b-form-checkbox>
                        <b-dropdown
                            size="sm"
                            variant="secondary"
                            dropleft
                        >
                            <template #button-content>
                                <font-awesome-icon :icon="['fad', 'cog']"/>
                            </template>
                            <b-dropdown-item @click="edit" :disabled="disabled">
                                <font-awesome-icon :icon="['fad', 'edit']" class="text-primary" fixed-width/>
                                Edit
                            </b-dropdown-item>
                            <b-dropdown-item @click="remove" :disabled="disabled">
                                <font-awesome-icon :icon="['fad', 'trash']" class="text-danger" fixed-width/>
                                Remove
                            </b-dropdown-item>
                        </b-dropdown>
                    </div>
                </div>
            </template>

            <div class="d-flex flex-row">
                <div class="drag-handle d-flex justify-content-center align-items-center ml-3 mr-3">
                    <font-awesome-icon
                        :icon="['fal', 'bars']"
                        size="2x"
                        fixed-width
                        style="color: gainsboro; cursor:grab"
                    />
                </div>

                <b-tabs
                    class="w-100"
                    nav-wrapper-class="slide-tabs-nav"
                    content-class="slide-tab-body"
                    card
                    fill
                >
                    <b-tab>
                        <template #title>
                            Slide Content
                            <span
                                v-if="contentHeightExceeded"
                                :id="`slide-height-warning-${item.id}`"
                                class="text-warning ml-1"
                                tabindex="0"
                                @click.stop
                            >
                                <font-awesome-icon :icon="['fas', 'exclamation-triangle']" fixed-width />
                            </span>
                            <b-popover
                                v-if="contentHeightExceeded"
                                :target="`slide-height-warning-${item.id}`"
                                triggers="hover focus"
                                placement="top"
                            >
                                Slide content height ({{ slideContentHeight }}px) exceeds 460px.
                            </b-popover>
                        </template>
                        <div v-if="item.question_id" ref="questionBlock">
                            <div v-if="item.question.type === 'text_block'">
                                <text-block :question="item.question" :is_walkthrough="true" :key="key"/>
                            </div>

                            <div v-if="item.question.type === 'media_block'">
                                <media-block
                                    class="media-block"
                                    :question="item.question"
                                    :is_walkthrough="true"
                                    :key="key"
                                />
                            </div>
                        </div>
                        <div class="d-flex align-items-center" v-else>
                            <template v-if="item.related_media_id">
                                <div v-if="item.related_media.type === 'audio'">
                                    <b-link :href="item.related_media.url" target="_blank" class="pl-2">
                                        <b-img
                                            :src="item.related_media.splash_url ? item.related_media.splash_url : AUDIO_ICON"
                                            width="75"
                                            height="auto"
                                            class="p-0"
                                            thumbnail
                                        />
                                    </b-link>
                                </div>
                                <div v-if="item.related_media.type === 'video'">
                                    <b-link :href="item.related_media.url" target="_blank" class="pl-2">
                                        <b-img
                                            :src="item.related_media.splash_url ? item.related_media.splash_url : VIDEO_ICON"
                                            width="75"
                                            height="auto"
                                            class="p-0"
                                            thumbnail
                                        />
                                    </b-link>
                                </div>
                                <div v-if="item.related_media.type === 'image'">
                                    <b-link :href="item.related_media.url" target="_blank" class="pl-2">
                                        <b-img
                                            :src="item.related_media.splash_url ? item.related_media.splash_url : item.related_media.url"
                                            width="75"
                                            height="auto"
                                            class="p-0"
                                            thumbnail
                                        />
                                    </b-link>
                                </div>
                                <div v-if="item.related_media.type === 'sims'">
                                    <b-link :href="item.related_media.url" target="_blank" class="pl-2">
                                        <b-img
                                            :src="item.related_media.splash_url ? item.related_media.splash_url : item.related_media.url"
                                            width="75"
                                            height="auto"
                                            class="p-0"
                                            thumbnail
                                        />
                                    </b-link>
                                </div>
                            </template>
                            <template v-else>
                                <b-link :href="item.url" target="_blank" class="pl-2">
                                    <b-img
                                        :src="`${item.splash_url}?sbp_cache_buster=${cacheBusterValue}`"
                                        width="75"
                                        height="auto"
                                        class="p-0"
                                        thumbnail
                                    />
                                </b-link>
                            </template>

                            <Title
                                class="flex-grow-1"
                                v-model="item.title"
                                :article="article"
                                :item="item"
                                variant="light"
                                @update="(title) => updateSlide({...item, title})"
                                :disabled="disabled"
                                placeholder="no asset title"
                            />

                            <Media-Badge class="ml-3" :type="item.related_media_id ? item.related_media.type: 'pdf'"/>
                            <Flag-Badge
                                v-if="item.related_media_id"
                                class="ml-3"
                                :language="item.related_media.language"
                            />
                        </div>
                    </b-tab>

                    <b-tab>
                        <template #title>Teacher Guide</template>
                        <TipTap
                            :publication="publication"
                            v-model="item.teacher_guide"
                            :readonly="disabled"
                            :menu_items="[
                                'bold',
                                'italic',
                                'underline',
                                'ordered_list',
                                'bullet_list',
                                'interactive_media_link',
                                'image',
                                'hard_break',
                                'highlight',
                                'highlight_orange',
                                'highlight_blue',
                                'highlight_green',
                                'highlight_black',
                                'highlight_white',
                                'page_break',
                                'edit_html',
                                'paragraph_alignment',
                            ]"
                            @save="updateSlide(item)"
                            @blur="updateSlide(item)"
                        />
                    </b-tab>
                </b-tabs>
            </div>
        </b-card>

        <related-media-item-modal
            v-model="show_related_media_item_modal"
            :item="item"
            :article="article"
            edit_type="walkthrough"
            @update="save"
            @hide="show_related_media_item_modal = false; cacheBusterValue = (new Date()).getTime()"
        />

        <question-modal
            v-model="show_question_modal"
            :unit="unit"
            :article="article"
            :question="item.question"
            :available_standards="[]"
            @update="save"
            @hide="show_question_modal = false"
        />
    </div>
</template>

<style>
/** videos are normally 100% width but this makes slides difficult to grab for re-ordering. */
.media-block video {
    max-width: 25%;
}

.slide-tabs-nav {
    background-color: #fff !important;
}

.slide-tab-body {
    border-left: 1px solid rgba(0, 0, 0, 0.125);
}
</style>

<script>
import moment from 'moment'
import CardID from '@/components/CardID'
import Title from '@/components/Title'
import RelatedMediaItemModal from '@/components/modals/article/related_media/RelatedMediaItemModal'
import TextBlock from '@/components/questions/TextBlock'
import MediaBlock from '@/components/questions/MediaBlock'
import QuestionModal from '@/components/modals/questions/QuestionModal'
import FlagBadge from '@/components/badges/FlagBadge'
import MediaBadge from '@/components/badges/MediaBadge'
import TipTap from '@/components/tiptap/TipTap'

export default {
    data: () => ({
        show_related_media_item_modal: false,
        show_question_modal: false,
        updating_walkthrough_media_order: false,
        key: 0,
        cacheBusterValue: (new Date()).getTime(),
        slideContentHeight: null,
        blockHeightObserver: null,
    }),
    props: [
        'publication',
        'article',
        'related_media_items',
        'item',
        'type',
        'unit',
        'disabled',
        'slideNumber',
    ],
    components: {
        TipTap,
        CardID,
        Title,
        RelatedMediaItemModal,
        TextBlock,
        MediaBlock,
        QuestionModal,
        FlagBadge,
        MediaBadge,
    },
    computed: {
        formattedType() {
            if (this.item.question_id) {
                return this.item.question.type.replace(/_/g, ' ')
            } else if (this.item.related_media_id) {
                return this.item.related_media.type
            } else {
                return 'PDF'
            }
        },
        identifier() {
            if (this.item.question_id) {
                return { 'id': this.item.question_id, 'type': 'Question'};
            } else if (this.item.related_media_id) {
                return { 'id': this.item.related_media_id, 'type': 'Media'};
            } else {
                return { 'id': this.item.id, 'type': ''};
            }
        },
        contentHeightExceeded() {
            return this.slideContentHeight !== null && this.slideContentHeight > 460
        },
        hasRasterProblems() {
            const hasWarningsOrErrors = this.item.raster_output
                && (this.item.raster_output.includes('warning') || this.item.raster_output.includes('error'))
            return this.formattedType === 'PDF' && hasWarningsOrErrors
        },
    },
    watch: {
        key() {
            this.slideContentHeight = null
            this.initBlockHeightCheck()
        },
    },
    methods: {
        initBlockHeightCheck() {
            if (this.blockHeightObserver) {
                this.blockHeightObserver.disconnect()
                this.blockHeightObserver = null
            }

            if (!this.isQuestionBlockHeightCheckable()) {
                this.slideContentHeight = null
                return
            }

            this.$nextTick(() => {
                const el = this.$refs.questionBlock
                if (!el) {
                    return
                }

                this.blockHeightObserver = new ResizeObserver(() => {
                    this.updateSlideContentHeight(el.offsetHeight)
                })
                this.blockHeightObserver.observe(el)
                this.updateSlideContentHeight(el.offsetHeight)
            })
        },
        isQuestionBlockHeightCheckable() {
            return this.item.question_id
                && ['text_block', 'media_block'].includes(this.item.question.type)
        },
        updateSlideContentHeight(height) {
            this.slideContentHeight = height
        },
        edit() {
            if (this.item.question_id) {
                this.show_question_modal = true
            } else {
                this.show_related_media_item_modal = true
            }
        },
        async save(slide) {
            if (this.item.question_id) {
                await this.updateQuestion(slide)
            } else {
                await this.updateSlide(slide)
            }
        },
        async remove() {
            const result = await this.$swal({
                html: "Are you sure you want to remove this slide?",
                showCancelButton: true,
                confirmButtonText: 'Remove Slide',
                confirmButtonColor: '#E67E22',
                cancelButtonText: 'Cancel',
                reverseButtons: true
            })
            if (result.value) {
                this.$emit('deleted')
            }
        },
        async updateQuestion(question) {
            try {
                const questionWithWalkthroughId = {...this.item, question, walkthroughId: this.article.id}
                await this.$store.dispatch('updateQuestion', questionWithWalkthroughId)

                // Vue can fail to detect when a child component should re-render.
                // This forces a refresh when necessary.
                this.key++
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to update walkthrough question: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            } finally {
                this.show_question_modal = false
            }
        },
        async updateSlide(media) {
            try {
                const mediaWithWalkthroughId = {...media, walkthroughId: this.article.id}
                await this.$store.dispatch('updateSlide', mediaWithWalkthroughId)
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to update walkthrough media: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            } finally {
                this.show_related_media_item_modal = false
            }
        },
        showRasterInfoPopup() {
            const message = this.item.raster_output ? this.item.raster_output : ''
            this.$swal({
                html: `
                    <div class="mb-2">
                        This slide encountered an issue during conversion to PNG. Please verify it looks correct.
                    </div>
                    <textarea style="width: 100%; min-height: 200px; font-size: 10px;">${message}</textarea>
                `,
                showCancelButton: false,
                confirmButtonColor: '#E67E22',
            });
        }
    },
    created() {
        this.AUDIO_ICON = '//cdn.studiesweekly.com/online/resources/pod_media/audio_default.png'
        this.VIDEO_ICON = '//cdn.studiesweekly.com/online/resources/pod_media/video_default.png'
    },
    mounted() {
        this.initBlockHeightCheck()
    },
    beforeDestroy() {
        if (this.blockHeightObserver) {
            this.blockHeightObserver.disconnect()
        }
    },
}
</script>
